/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import {deferSignalStateWrite} from '../util/template-state';

@Component({
  standalone: false,
  selector: 'adn-event-timer',
  template: `
    <span *ngIf="!isRefreshing() && countDown() >= 1"> Checking {{timerWord}} in {{countDown()}} seconds</span>
    <span *ngIf="isRefreshing()" class="fa fa-cog fa-spin"></span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventTimerComponent implements OnInit, OnDestroy {
  @Input() timerWord: string;
  @Input() timerStart: string;
  @Output() refreshRequested = new EventEmitter<void>();

  readonly isRefreshing = signal(false);
  readonly countDown = signal(0);

  private defaultRefreshTime: number;
  private refreshTime: number;
  private newExecutionsTimeout: any;
  private totalTimeTimeout: any;
  private destroyStateListener: () => void;
  private destroyed = false;

  constructor(private readonly uiRouter: UIRouter) {}

  ngOnInit(): void {
    this.defaultRefreshTime = parseInt(this.timerStart || '0', 10) || 1;
    this.timerWord = this.timerWord || 'statuses';

    this.destroyStateListener = this.uiRouter.transitionService.onSuccess({}, () => {
      this.cancelTimers();
      this.destroyStateListener();
    }) as () => void;
  }

  cancelTimers(): void {
    clearTimeout(this.newExecutionsTimeout);
    clearTimeout(this.totalTimeTimeout);
    this.refreshTime = null;
    // Defer countDown clear — sync 1→0 during parent CD NG0100s the *ngIf.
    deferSignalStateWrite(() => {
      if (!this.destroyed) {
        this.countDown.set(0);
      }
    });
  }

  setRefreshing(isRefreshing: boolean): void {
    // Parent report-requester flips this around HTTP; defer past checkNoChanges.
    deferSignalStateWrite(() => {
      if (!this.destroyed) {
        this.isRefreshing.set(isRefreshing);
      }
    });
  }

  refreshTimers(suppliedTime?: number): void {
    if (suppliedTime) {
      this.defaultRefreshTime = suppliedTime;
      this.refreshTime = suppliedTime;
    } else if (!this.refreshTime) {
      this.refreshTime = this.defaultRefreshTime;
    } else {
      this.refreshTime = Math.min(Math.ceil(this.refreshTime + (this.refreshTime / 2)), 30);
    }
    clearTimeout(this.totalTimeTimeout);
    this.tick(this.refreshTime);
    clearTimeout(this.newExecutionsTimeout);
    this.newExecutionsTimeout = setTimeout(() => {
      this.refreshRequested.emit();
    }, this.refreshTime * 1000);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.newExecutionsTimeout);
    clearTimeout(this.totalTimeTimeout);
    if (this.destroyStateListener) {
      this.destroyStateListener();
    }
  }

  private tick(newCountdown?: number): void {
    const next = Math.max(newCountdown !== undefined ? newCountdown : this.countDown() - 1, 1);
    deferSignalStateWrite(() => {
      if (!this.destroyed) {
        this.countDown.set(next);
      }
    });
    this.totalTimeTimeout = setTimeout(() => {
      if (this.refreshTime >= this.defaultRefreshTime) {
        this.tick();
      }
    }, 1000);
  }
}
